/**
 * What a failed request says to the person using the page.
 *
 * When a request gets no HTTP answer at all -- the connection dropped, the
 * server was restarting for a deploy, or the server answered in a way the
 * browser refuses to show the page -- fetch rejects with a TypeError whose
 * message is the browser's own wording: "Failed to fetch" in Chrome,
 * "NetworkError when attempting to fetch resource." in Firefox, "Load failed"
 * in Safari. Those strings went straight onto the screen, in English, from
 * every one of the 114 places the app calls fetch.
 *
 * Wrapping fetch once, at start-up, turns that one failure into one plain
 * sentence everywhere, without touching the call sites. Everything else
 * passes through untouched: real HTTP errors still resolve (the caller reads
 * the status and the server's message), and an AbortError is not a TypeError.
 */

export const NETWORK_FAILURE_MESSAGE =
  "Could not reach the server. Check your connection and try again.";

// The browsers' own wordings for "no response". Matched exactly: a TypeError
// raised by our own code inside a .then() must not be relabelled as network.
const BROWSER_NETWORK_MESSAGES = new Set([
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Load failed",
  "Network request failed",
]);

export class NetworkError extends Error {
  constructor(cause) {
    super(NETWORK_FAILURE_MESSAGE);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export function isNetworkFailure(err) {
  return err instanceof TypeError && BROWSER_NETWORK_MESSAGES.has(err.message);
}

export function installNetworkErrorMessages(target = typeof window !== "undefined" ? window : undefined) {
  if (!target || typeof target.fetch !== "function" || target.fetch.__networkErrorsInstalled) return;
  const original = target.fetch.bind(target);
  const wrapped = async (...args) => {
    try {
      return await original(...args);
    } catch (err) {
      if (isNetworkFailure(err)) {
        // Keep the browser's wording for whoever opens the console.
        // eslint-disable-next-line no-console
        console.warn("[network] request failed with no response:", err.message, args[0]);
        throw new NetworkError(err);
      }
      throw err;
    }
  };
  wrapped.__networkErrorsInstalled = true;
  target.fetch = wrapped;
}
